import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type DesignTheme = 
  | 'default'
  | 'cyberpunk'
  | 'matrix'
  | 'minimal'
  | 'brutalist'
  | 'retro'
  | 'vaporwave'
  | 'terminal'
  | 'neon'
  | 'stealth'
  | 'hologram'
  | 'monochrome'
  | 'synthwave'
  | 'blood-moon'
  | 'arctic'
  | 'desert'
  | 'ocean'
  | 'forest'
  | 'cosmic'
  | 'tokyo-night'
  | 'industrial';

interface ThemeColors {
  // Colors
  '--neon-cyan': string;
  '--neon-blue': string;
  '--neon-magenta': string;
  '--neon-purple': string;
  '--nixie-orange': string;
  '--nixie-amber': string;
  '--void-black': string;
  
  // Glass Effects
  '--glass-bg': string;
  '--glass-border': string;
  '--glass-blur': string;
  
  // Shadows & Glows
  '--glow-intensity': string;
  '--shadow-style': string;
  
  // Border Styles
  '--border-radius': string;
  '--border-width': string;
  
  // Background
  '--bg-pattern': string;
  '--bg-overlay': string;
  
  // Countdown Timer
  '--timer-bg': string;
  '--timer-border': string;
  '--timer-text-hours': string;
  '--timer-text-minutes': string;
  '--timer-text-seconds': string;
  '--timer-glow': string;
}

export const THEME_CONFIGS: Record<DesignTheme, { name: string; colors: ThemeColors; className: string }> = {
  default: {
    name: 'Default Holographic',
    className: 'theme-default',
    colors: {
      '--neon-cyan': '#00ffff',
      '--neon-blue': '#00d4ff',
      '--neon-magenta': '#ff00aa',
      '--neon-purple': '#9932cc',
      '--nixie-orange': '#ff6600',
      '--nixie-amber': '#ff8c32',
      '--void-black': '#000000',
      '--glass-bg': 'rgba(8, 8, 16, 0.85)',
      '--glass-border': 'rgba(0, 212, 255, 0.25)',
      '--glass-blur': '24px',
      '--glow-intensity': '20px',
      '--shadow-style': '0 8px 32px rgba(0, 255, 255, 0.15)',
      '--border-radius': '16px',
      '--border-width': '2px',
      '--bg-pattern': 'radial-gradient(circle at 20% 50%, rgba(0, 255, 255, 0.03) 0%, transparent 50%)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.95) 100%)',
      '--timer-bg': 'rgba(26, 26, 58, 1)',
      '--timer-border': 'rgba(0, 212, 255, 0.5)',
      '--timer-text-hours': '#00ffff',
      '--timer-text-minutes': '#9932cc',
      '--timer-text-seconds': '#ff00aa',
      '--timer-glow': '0 0 20px rgba(0, 255, 255, 0.4)',
    }
  },
  
  cyberpunk: {
    name: 'Cyberpunk 2077',
    className: 'theme-cyberpunk',
    colors: {
      '--neon-cyan': '#ff0080',
      '--neon-blue': '#00ffff',
      '--neon-magenta': '#ff00ff',
      '--neon-purple': '#8800ff',
      '--nixie-orange': '#ffff00',
      '--nixie-amber': '#ff00ff',
      '--void-black': '#0d0015',
      '--glass-bg': 'rgba(20, 0, 30, 0.95)',
      '--glass-border': 'rgba(255, 0, 128, 0.5)',
      '--glass-blur': '40px',
      '--glow-intensity': '40px',
      '--shadow-style': '0 0 60px rgba(255, 0, 128, 0.6), 0 0 20px rgba(0, 255, 255, 0.4)',
      '--border-radius': '4px',
      '--border-width': '3px',
      '--bg-pattern': 'repeating-linear-gradient(0deg, rgba(255,0,128,0.03) 0px, transparent 2px, transparent 4px, rgba(0,255,255,0.03) 6px)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(13,0,21,0.85) 0%, rgba(13,0,21,0.95) 100%)',
      '--timer-bg': 'rgba(30, 0, 40, 1)',
      '--timer-border': 'rgba(255, 0, 128, 0.8)',
      '--timer-text-hours': '#ff0080',
      '--timer-text-minutes': '#00ffff',
      '--timer-text-seconds': '#ff00ff',
      '--timer-glow': '0 0 30px rgba(255, 0, 128, 0.9)',
    }
  },
  
  matrix: {
    name: 'Matrix Code Rain',
    className: 'theme-matrix',
    colors: {
      '--neon-cyan': '#00ff41',
      '--neon-blue': '#00ff41',
      '--neon-magenta': '#39ff14',
      '--neon-purple': '#00cc33',
      '--nixie-orange': '#00ff41',
      '--nixie-amber': '#33ff66',
      '--void-black': '#000000',
      '--glass-bg': 'rgba(0, 20, 0, 0.98)',
      '--glass-border': 'rgba(0, 255, 65, 0.6)',
      '--glass-blur': '0px',
      '--glow-intensity': '15px',
      '--shadow-style': '0 0 20px rgba(0, 255, 65, 0.5), inset 0 0 20px rgba(0, 255, 65, 0.1)',
      '--border-radius': '0px',
      '--border-width': '2px',
      '--bg-pattern': 'repeating-linear-gradient(0deg, rgba(0,255,65,0.02) 0px, transparent 1px, transparent 2px)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,10,0,0.95) 100%)',
      '--timer-bg': 'rgba(0, 0, 0, 1)',
      '--timer-border': 'rgba(0, 255, 65, 0.8)',
      '--timer-text-hours': '#00ff41',
      '--timer-text-minutes': '#00ff41',
      '--timer-text-seconds': '#39ff14',
      '--timer-glow': '0 0 15px rgba(0, 255, 65, 0.7)',
    }
  },
  
  minimal: {
    name: 'Minimal Clean',
    className: 'theme-minimal',
    colors: {
      '--neon-cyan': '#888888',
      '--neon-blue': '#aaaaaa',
      '--neon-magenta': '#666666',
      '--neon-purple': '#999999',
      '--nixie-orange': '#cccccc',
      '--nixie-amber': '#bbbbbb',
      '--void-black': '#0a0a0a',
      '--glass-bg': 'rgba(20, 20, 20, 0.7)',
      '--glass-border': 'rgba(100, 100, 100, 0.2)',
      '--glass-blur': '8px',
      '--glow-intensity': '0px',
      '--shadow-style': '0 2px 8px rgba(0, 0, 0, 0.3)',
      '--border-radius': '8px',
      '--border-width': '1px',
      '--bg-pattern': 'none',
      '--bg-overlay': 'linear-gradient(180deg, rgba(10,10,10,1) 0%, rgba(15,15,15,1) 100%)',
      '--timer-bg': 'rgba(40, 40, 40, 1)',
      '--timer-border': 'rgba(120, 120, 120, 0.4)',
      '--timer-text-hours': '#aaaaaa',
      '--timer-text-minutes': '#999999',
      '--timer-text-seconds': '#888888',
      '--timer-glow': 'none',
    }
  },
  
  brutalist: {
    name: 'Brutalist Raw',
    className: 'theme-brutalist',
    colors: {
      '--neon-cyan': '#ffffff',
      '--neon-blue': '#ffff00',
      '--neon-magenta': '#ff0000',
      '--neon-purple': '#00ff00',
      '--nixie-orange': '#ff00ff',
      '--nixie-amber': '#00ffff',
      '--void-black': '#000000',
      '--glass-bg': 'rgba(0, 0, 0, 0.95)',
      '--glass-border': 'rgba(255, 255, 255, 1)',
      '--glass-blur': '0px',
      '--glow-intensity': '0px',
      '--shadow-style': 'none',
      '--border-radius': '0px',
      '--border-width': '4px',
      '--bg-pattern': 'repeating-linear-gradient(90deg, transparent 0px, transparent 49px, rgba(255,255,255,0.05) 49px, rgba(255,255,255,0.05) 50px)',
      '--bg-overlay': 'none',
      '--timer-bg': 'rgba(0, 0, 0, 1)',
      '--timer-border': 'rgba(255, 255, 255, 1)',
      '--timer-text-hours': '#ffff00',
      '--timer-text-minutes': '#ff0000',
      '--timer-text-seconds': '#00ff00',
      '--timer-glow': 'none',
    }
  },
  
  retro: {
    name: 'Retro CRT Terminal',
    className: 'theme-retro',
    colors: {
      '--neon-cyan': '#33ff33',
      '--neon-blue': '#33ff33',
      '--neon-magenta': '#33ff33',
      '--neon-purple': '#00cc00',
      '--nixie-orange': '#33ff33',
      '--nixie-amber': '#66ff66',
      '--void-black': '#000000',
      '--glass-bg': 'rgba(0, 15, 0, 0.98)',
      '--glass-border': 'rgba(51, 255, 51, 0.3)',
      '--glass-blur': '2px',
      '--glow-intensity': '8px',
      '--shadow-style': 'inset 0 0 100px rgba(0, 0, 0, 0.9), 0 0 10px rgba(51, 255, 51, 0.4)',
      '--border-radius': '12px',
      '--border-width': '1px',
      '--bg-pattern': 'repeating-linear-gradient(0deg, rgba(51,255,51,0.03) 0px, transparent 2px)',
      '--bg-overlay': 'radial-gradient(ellipse at center, rgba(0,15,0,0.8) 0%, rgba(0,0,0,1) 100%)',
      '--timer-bg': 'rgba(0, 20, 0, 1)',
      '--timer-border': 'rgba(51, 255, 51, 0.5)',
      '--timer-text-hours': '#33ff33',
      '--timer-text-minutes': '#33ff33',
      '--timer-text-seconds': '#66ff66',
      '--timer-glow': '0 0 12px rgba(51, 255, 51, 0.6)',
    }
  },
  
  vaporwave: {
    name: 'Vaporwave Aesthetic',
    className: 'theme-vaporwave',
    colors: {
      '--neon-cyan': '#01cdfe',
      '--neon-blue': '#05ffa1',
      '--neon-magenta': '#ff71ce',
      '--neon-purple': '#b967ff',
      '--nixie-orange': '#ffb86c',
      '--nixie-amber': '#fffb96',
      '--void-black': '#05182b',
      '--glass-bg': 'rgba(20, 40, 60, 0.8)',
      '--glass-border': 'rgba(1, 205, 254, 0.4)',
      '--glass-blur': '28px',
      '--glow-intensity': '30px',
      '--shadow-style': '0 8px 32px rgba(255, 113, 206, 0.3), 0 0 60px rgba(1, 205, 254, 0.2)',
      '--border-radius': '20px',
      '--border-width': '2px',
      '--bg-pattern': 'linear-gradient(45deg, rgba(1,205,254,0.05) 25%, transparent 25%, transparent 75%, rgba(255,113,206,0.05) 75%, rgba(255,113,206,0.05)), linear-gradient(-45deg, rgba(1,205,254,0.05) 25%, transparent 25%, transparent 75%, rgba(255,113,206,0.05) 75%, rgba(255,113,206,0.05))',
      '--bg-overlay': 'linear-gradient(180deg, rgba(5,24,43,0.9) 0%, rgba(20,40,60,0.95) 100%)',
      '--timer-bg': 'rgba(30, 50, 70, 1)',
      '--timer-border': 'rgba(1, 205, 254, 0.6)',
      '--timer-text-hours': '#01cdfe',
      '--timer-text-minutes': '#ff71ce',
      '--timer-text-seconds': '#b967ff',
      '--timer-glow': '0 0 25px rgba(1, 205, 254, 0.5)',
    }
  },
  
  terminal: {
    name: 'Hacker Terminal',
    className: 'theme-terminal',
    colors: {
      '--neon-cyan': '#39ff14',
      '--neon-blue': '#39ff14',
      '--neon-magenta': '#ccff00',
      '--neon-purple': '#00ff00',
      '--nixie-orange': '#39ff14',
      '--nixie-amber': '#66ff33',
      '--void-black': '#0c0c0c',
      '--glass-bg': 'rgba(15, 25, 15, 0.95)',
      '--glass-border': 'rgba(57, 255, 20, 0.4)',
      '--glass-blur': '4px',
      '--glow-intensity': '12px',
      '--shadow-style': '0 4px 16px rgba(57, 255, 20, 0.3)',
      '--border-radius': '4px',
      '--border-width': '1px',
      '--bg-pattern': 'repeating-linear-gradient(0deg, rgba(57,255,20,0.02) 0px, transparent 1px, transparent 3px)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(12,12,12,0.95) 0%, rgba(18,18,18,0.98) 100%)',
      '--timer-bg': 'rgba(0, 15, 0, 1)',
      '--timer-border': 'rgba(57, 255, 20, 0.6)',
      '--timer-text-hours': '#39ff14',
      '--timer-text-minutes': '#39ff14',
      '--timer-text-seconds': '#ccff00',
      '--timer-glow': '0 0 15px rgba(57, 255, 20, 0.5)',
    }
  },
  
  neon: {
    name: 'Neon Overdrive',
    className: 'theme-neon',
    colors: {
      '--neon-cyan': '#00ffff',
      '--neon-blue': '#0080ff',
      '--neon-magenta': '#ff00ff',
      '--neon-purple': '#ff00aa',
      '--nixie-orange': '#ff8800',
      '--nixie-amber': '#ffaa00',
      '--void-black': '#000000',
      '--glass-bg': 'rgba(10, 0, 20, 0.7)',
      '--glass-border': 'rgba(0, 255, 255, 0.8)',
      '--glass-blur': '32px',
      '--glow-intensity': '50px',
      '--shadow-style': '0 0 80px rgba(0, 255, 255, 0.8), 0 0 40px rgba(255, 0, 255, 0.6), 0 0 20px rgba(255, 136, 0, 0.4)',
      '--border-radius': '24px',
      '--border-width': '3px',
      '--bg-pattern': 'radial-gradient(circle at 50% 50%, rgba(0,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,0,255,0.08) 0%, transparent 50%)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(10,0,20,0.9) 100%)',
      '--timer-bg': 'rgba(20, 0, 30, 0.9)',
      '--timer-border': 'rgba(0, 255, 255, 1)',
      '--timer-text-hours': '#00ffff',
      '--timer-text-minutes': '#ff00ff',
      '--timer-text-seconds': '#ff8800',
      '--timer-glow': '0 0 40px rgba(0, 255, 255, 1), 0 0 20px rgba(255, 0, 255, 0.7)',
    }
  },
  
  stealth: {
    name: 'Stealth Mode',
    className: 'theme-stealth',
    colors: {
      '--neon-cyan': '#1a1a2e',
      '--neon-blue': '#16213e',
      '--neon-magenta': '#0f3460',
      '--neon-purple': '#533483',
      '--nixie-orange': '#e94560',
      '--nixie-amber': '#ff6b6b',
      '--void-black': '#0a0a0a',
      '--glass-bg': 'rgba(15, 15, 25, 0.9)',
      '--glass-border': 'rgba(30, 30, 50, 0.5)',
      '--glass-blur': '16px',
      '--glow-intensity': '4px',
      '--shadow-style': '0 4px 20px rgba(0, 0, 0, 0.5)',
      '--border-radius': '12px',
      '--border-width': '1px',
      '--bg-pattern': 'none',
      '--bg-overlay': 'linear-gradient(180deg, rgba(10,10,10,1) 0%, rgba(15,15,25,0.98) 100%)',
      '--timer-bg': 'rgba(20, 20, 30, 1)',
      '--timer-border': 'rgba(40, 40, 60, 0.6)',
      '--timer-text-hours': '#16213e',
      '--timer-text-minutes': '#0f3460',
      '--timer-text-seconds': '#533483',
      '--timer-glow': '0 0 8px rgba(30, 30, 50, 0.4)',
    }
  },
  
  hologram: {
    name: 'Hologram Projection',
    className: 'theme-hologram',
    colors: {
      '--neon-cyan': '#00d9ff',
      '--neon-blue': '#0099ff',
      '--neon-magenta': '#66ccff',
      '--neon-purple': '#3366ff',
      '--nixie-orange': '#00ccff',
      '--nixie-amber': '#33ddff',
      '--void-black': '#000510',
      '--glass-bg': 'rgba(0, 10, 30, 0.5)',
      '--glass-border': 'rgba(0, 217, 255, 0.3)',
      '--glass-blur': '20px',
      '--glow-intensity': '25px',
      '--shadow-style': '0 0 40px rgba(0, 217, 255, 0.4), inset 0 0 40px rgba(0, 217, 255, 0.1)',
      '--border-radius': '8px',
      '--border-width': '1px',
      '--bg-pattern': 'repeating-linear-gradient(0deg, transparent 0px, transparent 3px, rgba(0,217,255,0.03) 3px, rgba(0,217,255,0.03) 4px)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(0,5,16,0.9) 0%, rgba(0,10,30,0.95) 100%)',
      '--timer-bg': 'rgba(0, 15, 40, 0.7)',
      '--timer-border': 'rgba(0, 217, 255, 0.5)',
      '--timer-text-hours': '#00d9ff',
      '--timer-text-minutes': '#66ccff',
      '--timer-text-seconds': '#0099ff',
      '--timer-glow': '0 0 30px rgba(0, 217, 255, 0.6)',
    }
  },
  
  monochrome: {
    name: 'Monochrome',
    className: 'theme-monochrome',
    colors: {
      '--neon-cyan': '#ffffff',
      '--neon-blue': '#cccccc',
      '--neon-magenta': '#999999',
      '--neon-purple': '#666666',
      '--nixie-orange': '#ffffff',
      '--nixie-amber': '#eeeeee',
      '--void-black': '#000000',
      '--glass-bg': 'rgba(0, 0, 0, 0.8)',
      '--glass-border': 'rgba(255, 255, 255, 0.2)',
      '--glass-blur': '12px',
      '--glow-intensity': '8px',
      '--shadow-style': '0 4px 16px rgba(0, 0, 0, 0.5)',
      '--border-radius': '8px',
      '--border-width': '1px',
      '--bg-pattern': 'none',
      '--bg-overlay': 'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(20,20,20,0.98) 100%)',
      '--timer-bg': 'rgba(30, 30, 30, 1)',
      '--timer-border': 'rgba(255, 255, 255, 0.3)',
      '--timer-text-hours': '#ffffff',
      '--timer-text-minutes': '#cccccc',
      '--timer-text-seconds': '#999999',
      '--timer-glow': '0 0 10px rgba(255, 255, 255, 0.3)',
    }
  },
  
  synthwave: {
    name: 'Synthwave Sunset',
    className: 'theme-synthwave',
    colors: {
      '--neon-cyan': '#ff6ec7',
      '--neon-blue': '#7d5fff',
      '--neon-magenta': '#fd1d53',
      '--neon-purple': '#c471ed',
      '--nixie-orange': '#f97e72',
      '--nixie-amber': '#ffd700',
      '--void-black': '#2b1b3d',
      '--glass-bg': 'rgba(40, 20, 50, 0.85)',
      '--glass-border': 'rgba(255, 110, 199, 0.5)',
      '--glass-blur': '36px',
      '--glow-intensity': '35px',
      '--shadow-style': '0 0 50px rgba(255, 110, 199, 0.5), 0 0 25px rgba(125, 95, 255, 0.4)',
      '--border-radius': '16px',
      '--border-width': '2px',
      '--bg-pattern': 'linear-gradient(180deg, rgba(125,95,255,0.1) 0%, rgba(255,110,199,0.05) 100%)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(43,27,61,0.9) 0%, rgba(60,30,80,0.95) 100%)',
      '--timer-bg': 'rgba(50, 25, 60, 1)',
      '--timer-border': 'rgba(255, 110, 199, 0.7)',
      '--timer-text-hours': '#ff6ec7',
      '--timer-text-minutes': '#7d5fff',
      '--timer-text-seconds': '#ffd700',
      '--timer-glow': '0 0 35px rgba(255, 110, 199, 0.7)',
    }
  },
  
  'blood-moon': {
    name: 'Blood Moon',
    className: 'theme-blood-moon',
    colors: {
      '--neon-cyan': '#ff0000',
      '--neon-blue': '#cc0000',
      '--neon-magenta': '#8b0000',
      '--neon-purple': '#660000',
      '--nixie-orange': '#ff4444',
      '--nixie-amber': '#ff6666',
      '--void-black': '#0a0000',
      '--glass-bg': 'rgba(30, 0, 0, 0.95)',
      '--glass-border': 'rgba(255, 0, 0, 0.5)',
      '--glass-blur': '18px',
      '--glow-intensity': '28px',
      '--shadow-style': '0 0 40px rgba(255, 0, 0, 0.6), inset 0 0 20px rgba(139, 0, 0, 0.3)',
      '--border-radius': '12px',
      '--border-width': '2px',
      '--bg-pattern': 'radial-gradient(circle at 50% 20%, rgba(255,0,0,0.08) 0%, transparent 50%)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(10,0,0,0.95) 0%, rgba(20,0,0,0.98) 100%)',
      '--timer-bg': 'rgba(40, 0, 0, 1)',
      '--timer-border': 'rgba(255, 0, 0, 0.7)',
      '--timer-text-hours': '#ff0000',
      '--timer-text-minutes': '#cc0000',
      '--timer-text-seconds': '#8b0000',
      '--timer-glow': '0 0 30px rgba(255, 0, 0, 0.8)',
    }
  },
  
  arctic: {
    name: 'Arctic Freeze',
    className: 'theme-arctic',
    colors: {
      '--neon-cyan': '#00e5ff',
      '--neon-blue': '#82f4ff',
      '--neon-magenta': '#b3f5ff',
      '--neon-purple': '#4dd0e1',
      '--nixie-orange': '#ffffff',
      '--nixie-amber': '#e0f7fa',
      '--void-black': '#001820',
      '--glass-bg': 'rgba(0, 30, 40, 0.8)',
      '--glass-border': 'rgba(0, 229, 255, 0.4)',
      '--glass-blur': '30px',
      '--glow-intensity': '22px',
      '--shadow-style': '0 8px 32px rgba(0, 229, 255, 0.25), 0 0 50px rgba(130, 244, 255, 0.15)',
      '--border-radius': '20px',
      '--border-width': '1px',
      '--bg-pattern': 'radial-gradient(circle at 30% 40%, rgba(0,229,255,0.05) 0%, transparent 60%)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(0,24,32,0.9) 0%, rgba(0,40,50,0.95) 100%)',
      '--timer-bg': 'rgba(0, 40, 50, 0.9)',
      '--timer-border': 'rgba(0, 229, 255, 0.6)',
      '--timer-text-hours': '#00e5ff',
      '--timer-text-minutes': '#82f4ff',
      '--timer-text-seconds': '#b3f5ff',
      '--timer-glow': '0 0 25px rgba(0, 229, 255, 0.5)',
    }
  },
  
  desert: {
    name: 'Desert Heat',
    className: 'theme-desert',
    colors: {
      '--neon-cyan': '#ffad33',
      '--neon-blue': '#ff8c1a',
      '--neon-magenta': '#ff7043',
      '--neon-purple': '#d84315',
      '--nixie-orange': '#ff6f00',
      '--nixie-amber': '#ffa726',
      '--void-black': '#1a0f00',
      '--glass-bg': 'rgba(40, 25, 10, 0.85)',
      '--glass-border': 'rgba(255, 140, 26, 0.4)',
      '--glass-blur': '22px',
      '--glow-intensity': '18px',
      '--shadow-style': '0 8px 24px rgba(255, 112, 67, 0.3)',
      '--border-radius': '14px',
      '--border-width': '2px',
      '--bg-pattern': 'radial-gradient(ellipse at 70% 30%, rgba(255,140,26,0.06) 0%, transparent 60%)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(26,15,0,0.95) 0%, rgba(40,20,5,0.98) 100%)',
      '--timer-bg': 'rgba(50, 30, 10, 1)',
      '--timer-border': 'rgba(255, 140, 26, 0.6)',
      '--timer-text-hours': '#ffad33',
      '--timer-text-minutes': '#ff8c1a',
      '--timer-text-seconds': '#d84315',
      '--timer-glow': '0 0 20px rgba(255, 140, 26, 0.5)',
    }
  },
  
  ocean: {
    name: 'Ocean Depths',
    className: 'theme-ocean',
    colors: {
      '--neon-cyan': '#00bcd4',
      '--neon-blue': '#0288d1',
      '--neon-magenta': '#0097a7',
      '--neon-purple': '#006064',
      '--nixie-orange': '#26c6da',
      '--nixie-amber': '#4dd0e1',
      '--void-black': '#001a1f',
      '--glass-bg': 'rgba(0, 25, 35, 0.88)',
      '--glass-border': 'rgba(0, 188, 212, 0.35)',
      '--glass-blur': '26px',
      '--glow-intensity': '16px',
      '--shadow-style': '0 8px 28px rgba(0, 188, 212, 0.2), inset 0 1px 0 rgba(77, 208, 225, 0.15)',
      '--border-radius': '18px',
      '--border-width': '1px',
      '--bg-pattern': 'repeating-linear-gradient(90deg, transparent 0px, transparent 40px, rgba(0,188,212,0.03) 40px, rgba(0,188,212,0.03) 80px)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(0,26,31,0.92) 0%, rgba(0,35,45,0.96) 100%)',
      '--timer-bg': 'rgba(0, 35, 45, 1)',
      '--timer-border': 'rgba(0, 188, 212, 0.5)',
      '--timer-text-hours': '#00bcd4',
      '--timer-text-minutes': '#0288d1',
      '--timer-text-seconds': '#26c6da',
      '--timer-glow': '0 0 18px rgba(0, 188, 212, 0.4)',
    }
  },
  
  forest: {
    name: 'Forest Grove',
    className: 'theme-forest',
    colors: {
      '--neon-cyan': '#4caf50',
      '--neon-blue': '#66bb6a',
      '--neon-magenta': '#81c784',
      '--neon-purple': '#2e7d32',
      '--nixie-orange': '#8bc34a',
      '--nixie-amber': '#aed581',
      '--void-black': '#0a1a0a',
      '--glass-bg': 'rgba(15, 30, 15, 0.9)',
      '--glass-border': 'rgba(76, 175, 80, 0.3)',
      '--glass-blur': '20px',
      '--glow-intensity': '12px',
      '--shadow-style': '0 8px 24px rgba(46, 125, 50, 0.25)',
      '--border-radius': '16px',
      '--border-width': '2px',
      '--bg-pattern': 'radial-gradient(circle at 40% 60%, rgba(76,175,80,0.04) 0%, transparent 50%)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(10,26,10,0.95) 0%, rgba(20,35,20,0.97) 100%)',
      '--timer-bg': 'rgba(20, 35, 20, 1)',
      '--timer-border': 'rgba(76, 175, 80, 0.5)',
      '--timer-text-hours': '#4caf50',
      '--timer-text-minutes': '#66bb6a',
      '--timer-text-seconds': '#8bc34a',
      '--timer-glow': '0 0 15px rgba(76, 175, 80, 0.4)',
    }
  },
  
  cosmic: {
    name: 'Cosmic Nebula',
    className: 'theme-cosmic',
    colors: {
      '--neon-cyan': '#9c27b0',
      '--neon-blue': '#673ab7',
      '--neon-magenta': '#e91e63',
      '--neon-purple': '#3f51b5',
      '--nixie-orange': '#ff4081',
      '--nixie-amber': '#ce93d8',
      '--void-black': '#0d0015',
      '--glass-bg': 'rgba(15, 5, 30, 0.82)',
      '--glass-border': 'rgba(156, 39, 176, 0.45)',
      '--glass-blur': '38px',
      '--glow-intensity': '32px',
      '--shadow-style': '0 0 60px rgba(156, 39, 176, 0.4), 0 0 30px rgba(103, 58, 183, 0.3)',
      '--border-radius': '22px',
      '--border-width': '2px',
      '--bg-pattern': 'radial-gradient(ellipse at 60% 40%, rgba(156,39,176,0.08) 0%, transparent 50%), radial-gradient(ellipse at 20% 80%, rgba(103,58,183,0.06) 0%, transparent 50%)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(13,0,21,0.88) 0%, rgba(20,5,35,0.92) 100%)',
      '--timer-bg': 'rgba(20, 5, 35, 0.95)',
      '--timer-border': 'rgba(156, 39, 176, 0.7)',
      '--timer-text-hours': '#9c27b0',
      '--timer-text-minutes': '#673ab7',
      '--timer-text-seconds': '#e91e63',
      '--timer-glow': '0 0 35px rgba(156, 39, 176, 0.6)',
    }
  },
  
  'tokyo-night': {
    name: 'Tokyo Night',
    className: 'theme-tokyo-night',
    colors: {
      '--neon-cyan': '#7dcfff',
      '--neon-blue': '#2ac3de',
      '--neon-magenta': '#bb9af7',
      '--neon-purple': '#9d7cd8',
      '--nixie-orange': '#ff9e64',
      '--nixie-amber': '#e0af68',
      '--void-black': '#1a1b26',
      '--glass-bg': 'rgba(30, 30, 46, 0.85)',
      '--glass-border': 'rgba(125, 207, 255, 0.4)',
      '--glass-blur': '25px',
      '--glow-intensity': '24px',
      '--shadow-style': '0 8px 32px rgba(125, 207, 255, 0.2), 0 0 40px rgba(187, 154, 247, 0.15)',
      '--border-radius': '14px',
      '--border-width': '2px',
      '--bg-pattern': 'repeating-linear-gradient(45deg, transparent 0px, transparent 20px, rgba(125,207,255,0.02) 20px, rgba(125,207,255,0.02) 40px)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(26,27,38,0.93) 0%, rgba(35,36,50,0.96) 100%)',
      '--timer-bg': 'rgba(35, 36, 50, 1)',
      '--timer-border': 'rgba(125, 207, 255, 0.6)',
      '--timer-text-hours': '#7dcfff',
      '--timer-text-minutes': '#bb9af7',
      '--timer-text-seconds': '#ff9e64',
      '--timer-glow': '0 0 28px rgba(125, 207, 255, 0.5)',
    }
  },
  
  industrial: {
    name: 'Industrial Steel',
    className: 'theme-industrial',
    colors: {
      '--neon-cyan': '#b0bec5',
      '--neon-blue': '#78909c',
      '--neon-magenta': '#ffeb3b',
      '--neon-purple': '#546e7a',
      '--nixie-orange': '#ffc107',
      '--nixie-amber': '#ffd54f',
      '--void-black': '#1c1c1c',
      '--glass-bg': 'rgba(35, 35, 35, 0.9)',
      '--glass-border': 'rgba(176, 190, 197, 0.3)',
      '--glass-blur': '10px',
      '--glow-intensity': '8px',
      '--shadow-style': '0 4px 16px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
      '--border-radius': '4px',
      '--border-width': '3px',
      '--bg-pattern': 'repeating-linear-gradient(0deg, transparent 0px, transparent 9px, rgba(255,235,59,0.02) 9px, rgba(255,235,59,0.02) 10px)',
      '--bg-overlay': 'linear-gradient(180deg, rgba(28,28,28,0.98) 0%, rgba(40,40,40,0.99) 100%)',
      '--timer-bg': 'rgba(45, 45, 45, 1)',
      '--timer-border': 'rgba(255, 235, 59, 0.5)',
      '--timer-text-hours': '#b0bec5',
      '--timer-text-minutes': '#ffeb3b',
      '--timer-text-seconds': '#ffc107',
      '--timer-glow': '0 0 10px rgba(255, 235, 59, 0.3)',
    }
  },
};

interface ThemeContextType {
  theme: DesignTheme;
  setTheme: (theme: DesignTheme) => void;
  toggleTheme: () => void;
  applyTheme: (theme: DesignTheme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Load saved theme from localStorage
  const [theme, setThemeState] = useState<DesignTheme>(() => {
    const saved = localStorage.getItem('nt3d_theme');
    return (saved && saved in THEME_CONFIGS) ? saved as DesignTheme : 'default';
  });

  // Apply theme CSS variables AND body class to document root
  const applyTheme = (themeKey: DesignTheme) => {
    const themeConfig = THEME_CONFIGS[themeKey];
    const root = document.documentElement;
    const body = document.body;
    
    // Apply CSS variables
    Object.entries(themeConfig.colors).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
    
    // Remove all theme classes
    Object.values(THEME_CONFIGS).forEach(config => {
      body.classList.remove(config.className);
    });
    
    // Add new theme class
    body.classList.add(themeConfig.className);
    
    console.log(`[ThemeContext] Applied theme: ${themeConfig.name}`);
  };

  // Set theme and persist to localStorage
  const setTheme = (newTheme: DesignTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('nt3d_theme', newTheme);
    applyTheme(newTheme);
  };

  const toggleTheme = () => {
    const themes = Object.keys(THEME_CONFIGS) as DesignTheme[];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  // Apply theme on mount
  useEffect(() => {
    applyTheme(theme);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, applyTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
